import { KeyValueObject } from '@questwork/q-utilities'
import { Logo } from '../logo'

const updateAllowedProps = [
  'active',
  'contactPerson',
  'consent',
  'content',
  'description',
  'displayName',
  'email',
  'fax',
  'logo',
  'name',
  'pdfUrl',
  'remarks',
  'tel',
  'website',
]

class RegistrationGroup {
  constructor(options = {}) {
    options = options || {}
    this.id = options.id || options._id
    this._meta = KeyValueObject.initOnlyValidFromArray(options._meta)
    this.active = options.active || false
    this.address = options.address
    this.boothNum = options.boothNum
    this.category = options.category
    this.code = options.code
    this.consent = options.consent
    this.content = options.content || []
    this.coordinator = options.coordinator
    this.coordinatorId = options.coordinatorId
    this.created = options.created || (new Date()).valueOf()
    this.description = getDescription(options)
    this.displayName = getDisplayName(options)
    this.email = options.email
    this.eventShortCode = options.eventShortCode
    this.fax = options.fax
    this.logo = Logo.init(options.logo)
    this.modified = options.modified || (new Date()).valueOf()
    this.name = options.name
    this.pdfUrl = options.pdfUrl || ''
    this.ranking = options.ranking
    this.remarks = KeyValueObject.initOnlyValidFromArray(options.remarks)
    this.tel = options.tel
    this.tenantCode = options.tenantCode
    this.website = options.website
  }

  // Class methods
  static init(options) {
    if (options instanceof RegistrationGroup) {
      return options
    }
    const instance = new RegistrationGroup(options)
    return instance.isValid ? instance : null
  }
  static initFromArray(arr) {
    if (Array.isArray(arr)) {
      return arr.map(this.init)
    }
    return []
  }
  static initOnlyValidFromArray(arr = []) {
    return this.initFromArray(arr).filter((i) => i)
  }

  // getters
  get isValid() {
    return !!this.code
  }
  get meta() {
    return KeyValueObject.toObject(this._meta || [])
  }

  appendMaterial(material) {
    this._meta = KeyValueObject.appendRecord(this._meta, 'materials', [material])
    return this
  }
  deleteMaterial(material) {
    let materials = this.getMaterials()
    if (Array.isArray(materials)) {
      materials = materials.filter((i) => {
        return i.created !== material.created
      })
      this.setMetaByKey('materials', materials)
    }
    return this
  }
  editMaterial(material) {
    let materials = this.getMaterials()
    let match = false
    materials = materials.map((m) => {
      if (m.created === material.created) {
        match = true
        return material
      }
      return m
    })
    if (!match) {
      materials.push({
        ...material,
        created: material.modified,
      })
    }
    this.setMetaByKey('materials', materials)
    return this
  }
  getLogoAssetFullPath() {
    return this.logo ? this.logo.getAssetFullPath() : null
  }
  getMaterials() {
    return this.meta.materials || []
  }
  getMeta() {
    return this._meta || []
  }
  getMetaByKey(key) {
    return (this._meta || []).find((m) => m.key === key)
  }
  isExhibitor() {
    return (this.category === 'exhibitor' || this.category === 'sponsor')
  }
  isSameId(id) {
    return this.id === id || this._id === id
  }
  setLogoAssetFullPath(url) {
    if (this.logo) {
      this.logo.setAssetFullPath(url)
    }
    return this
  }
  setMaterials(materials) {
    return this.setMetaByKey('materials', materials)
  }
  setMeta(meta) {
    this._meta = KeyValueObject.fromObject(meta)
    return this
  }
  setMetaByKey(key, value) {
    this._meta = KeyValueObject.updateOrInsertRecord(this._meta, key, value)
    return this
  }
  setModified() {
    this.modified = (new Date()).valueOf()
    return this
  }

  updateRegistrationGroup(update) {
    Object.keys(update).forEach((key) => {
      if (updateAllowedProps.includes(key)) {
        if (key === 'description') {
          if (Array.isArray(update[key])) {
            this.content = update[key]
          }
          this.content = (update[key] || '').split('\n')
        } else if (key === 'displayName') {
          const meta = this._meta.find((i) => i.key === 'displayName')
          if (meta) {
            meta.value = update[key]
          } else {
            this._meta.push({
              key: 'displayName', value: update[key]
            })
          }
        } else if (key === 'logo') {
          this.logo.updateLogo(update[key])
        } else if (key === 'remarks') {
          this.remarks = KeyValueObject.initOnlyValidFromArray(update[key])
        } else {
          this[key] = update[key]
        }
      }
    })
    return this.setModified()
  }

  validateProfile(arr = []) {
    const validProfile = arr.reduce((acc, i) => {
      if (typeof this[i] === 'function') {
        return acc && !!this[i]()
      }
      return acc && !!this[i]
    }, true)
    return this.setMetaByKey('validProfile', validProfile)
  }
}

function getDescription(options) {
  return (options.content || []).join('\n')
}

function getDisplayName(options) {
  const meta = (options._meta || []).find((i) => i.key === 'displayName')
  if (meta) {
    return meta.value
  }
  const remark = (options.remarks || []).find((i) => i.key === 'displayName')
  if (remark) {
    return remark.value
  }
  return options.name
}

export default {
  RegistrationGroup
}
export {
  RegistrationGroup
}
